import 'braid-design-system/reset';
import { ApolloProvider } from '@apollo/client';
import { linkContextDestructor } from '@seek/apac-candidate-header-footer';
import type { GenericEvent } from '@seek/data-tracking-event-catalogue';
import { Hubble, HubbleProvider } from '@seek/hubble';
import { VocabProvider } from '@vocab/react';
import {
  BraidProvider,
  makeLinkComponent,
  ToastProvider,
} from 'braid-design-system';
import seekJobsTheme from 'braid-design-system/themes/seekJobs';
import { HelmetProvider } from 'react-helmet-async';
import loadable from 'sku/@loadable/component';

import AnalyticsProvider from 'src/analytics/AnalyticsProvider';
import createAnalytics from 'src/analytics/adapter/';
import { appName } from 'src/config';
import { SAVED_SEARCH_ROUTE_NAME } from 'src/constants';
import getClient from 'src/getGraphqlClient';
import { FeatureFlagProviderWrapper } from 'src/hooks/experiments/FeatureFlagProviderWrapper';
import useExperiments from 'src/hooks/experiments/useExperiments';
import type { SkuEnvironment } from 'src/types';
import getJobSeekerId from 'src/utils/getJobSeekerId';
import { getCountryFromSite, getZoneFromCountry } from 'src/utils/melwaysUtils';

import useCustomLink from '../../hooks/useCustomLink';
import { mapLinkContextToTrackingContext } from '../HeaderFooterLinks/HeaderFooterLinks';

import AppConfigProvider, { type AppConfig } from './AppConfigProvider';
import { ExperimentsProvider } from './ExperimentContext';

const SaveSearchContainer = loadable(
  () => import('../SavedSearch/Container/SavedSearchesContainer'),
);

const RecommendationContainer = loadable(
  () => import('../Recommendations/RecommendationsContainer'),
);

type AppProps = Omit<AppConfig, 'country'>;

export default (appProps: AppProps) => {
  const {
    site,
    brand,
    environment,
    httpLink,
    route,
    experimentEnabled,
    analyticsExperimentId,
    graphqlEndpoint,
    experimentsConfig,
    experimentApiUrl,
    cookieDomain,
    language,
  } = appProps;
  const country = getCountryFromSite(site);
  const apolloClient = getClient({
    environment,
    httpLink,
    brand,
    country,
    graphqlEndpoint,
  });
  const { isExperimentReady, experiments } = useExperiments({
    experimentEnabled,
    experimentsConfig,
    experimentApiUrl,
    cookieDomain,
  });
  const analytics = createAnalytics(route, {
    experimentId: analyticsExperimentId,
    experiments,
    zone: getZoneFromCountry(country),
    siteSection: 'discover',
    siteLanguage: language,
    isLoggedIn: true,
    experimentsEligible: [],
    hubbleExperiments: {},
  });

  const CustomLink = makeLinkComponent((originalProps, ref) => {
    const [{ href, rel, ...restProps }, headerFooterLinkContext] =
      linkContextDestructor(originalProps);

    if (headerFooterLinkContext) {
      return (
        <a
          rel={rel ? 'nofollow' : undefined}
          ref={ref}
          href={href}
          {...restProps}
          onClick={(...arg) => {
            restProps.onClick?.(...arg);
            analytics.headerFooterLinkClicked(
              mapLinkContextToTrackingContext({
                linkContext: headerFooterLinkContext,
              }),
            );
          }}
        />
      );
    }

    const localizedHref = useCustomLink(href);

    return <a ref={ref} href={localizedHref} {...restProps} />;
  });

  const Content =
    route === SAVED_SEARCH_ROUTE_NAME
      ? SaveSearchContainer
      : RecommendationContainer;

  const CustomHubbleProvider = ({ children }: { children: JSX.Element }) => {
    if (typeof window === 'undefined' || !window.document) {
      return <>{children}</>;
    }
    const isProduction =
      (appProps.environment as SkuEnvironment) === 'production';

    const hubble = new Hubble({
      appName,
      appVersion: window.APP_CONFIG.version || 'local',
      brand: appProps.brand,
      country: country.toUpperCase() as GenericEvent['country'],
      isProduction,
      endpoint: isProduction
        ? 'https://web.aips-sol.com/v1/events'
        : 'https://web-staging.sol-data.com/v1/events',
      experience: 'candidate',
      loginId: getJobSeekerId() || '',
    });

    return <HubbleProvider hubble={hubble}>{children}</HubbleProvider>;
  };

  return (
    <AppConfigProvider value={{ ...appProps, country }}>
      <VocabProvider language={language} locale={country}>
        <ExperimentsProvider value={{ isExperimentReady }}>
          <AnalyticsProvider analytics={analytics}>
            <ApolloProvider client={apolloClient}>
              <CustomHubbleProvider>
                <BraidProvider theme={seekJobsTheme} linkComponent={CustomLink}>
                  <ToastProvider>
                    <HelmetProvider>
                      <FeatureFlagProviderWrapper>
                        {isExperimentReady && <Content />}
                      </FeatureFlagProviderWrapper>
                    </HelmetProvider>
                  </ToastProvider>
                </BraidProvider>
              </CustomHubbleProvider>
            </ApolloProvider>
          </AnalyticsProvider>
        </ExperimentsProvider>
      </VocabProvider>
    </AppConfigProvider>
  );
};
